/**
 * @spec docs/specs/features/staking-position-ui.md#wallet-integration
 * Wallet status indicator with dropdown to switch between connected wallets
 * and modal to view full address details
 */
import { useCallback, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { cn } from '@/lib/cn';
import { routes } from '@/lib/routes';
import { useSelectedWallet } from '@/hooks/useSelectedWallet';
import { WalletAddress } from '@/components/ui/WalletAddress';
import { Dropdown, type DropdownItem } from '@/components/ui/Dropdown';
import { WalletDetailModal } from '@/components/wallet/WalletDetailModal';
import WalletIcon from '@/assets/icons/wallet.svg?react';
import CheckIcon from '@/assets/icons/check.svg?react';

export interface WalletStatusProps {
  /** Custom className for the container */
  className?: string;
}

/**
 * Get human-readable label for wallet client type
 */
function getWalletTypeLabel(clientType: string): string {
  const labels: Record<string, string> = {
    privy: 'Privy Wallet',
    metamask: 'MetaMask',
    coinbase_wallet: 'Coinbase Wallet',
    wallet_connect: 'WalletConnect',
    rainbow: 'Rainbow',
    phantom: 'Phantom',
  };
  return labels[clientType] ?? clientType;
}

/**
 * Displays the connected wallet with dropdown to switch wallets
 *
 * Features:
 * - Shows currently selected wallet address
 * - Single wallet: click opens detail modal with full address + copy + explorer link
 * - Multiple wallets: dropdown to switch, plus "Wallet Details" item opens modal
 * - Click outside or Escape closes dropdown/modal
 */
export function WalletStatus({ className }: WalletStatusProps) {
  const navigate = useNavigate();
  const { address, wallets, setSelectedAddress } = useSelectedWallet();
  const [isModalOpen, setIsModalOpen] = useState(false);

  // Transform wallets into dropdown items (only used for multi-wallet)
  const dropdownItems = useMemo((): DropdownItem<string>[] => {
    const items: DropdownItem<string>[] = wallets.map((wallet) => {
      const isSelected = wallet.address.toLowerCase() === address?.toLowerCase();
      return {
        id: wallet.address,
        label: <WalletAddress address={wallet.address} chars={6} />,
        secondaryLabel: getWalletTypeLabel(wallet.walletClientType),
        trailing: isSelected ? <CheckIcon className="w-4 h-4 text-accent-purple" /> : undefined,
      };
    });

    // Add "Wallet Details" item at the bottom
    items.push({
      id: '__wallet_details__',
      label: <span className="text-accent-purple">Wallet Details</span>,
      secondaryLabel: 'View full address',
    });

    return items;
  }, [wallets, address]);

  const handleWalletSelect = useCallback(
    (walletAddress: string) => {
      if (walletAddress === '__wallet_details__') {
        setIsModalOpen(true);
        return;
      }
      setSelectedAddress(walletAddress);
      navigate(routes.address(walletAddress));
    },
    [setSelectedAddress, navigate]
  );

  const handleModalWalletSelect = useCallback(
    (walletAddress: string) => {
      setSelectedAddress(walletAddress);
      navigate(routes.address(walletAddress));
    },
    [setSelectedAddress, navigate]
  );

  if (!address) {
    return null;
  }

  const hasMultipleWallets = wallets.length > 1;

  const trigger = (
    <>
      <WalletIcon className="h-4 w-4 text-accent-purple" />
      <WalletAddress address={address} className="text-text-primary" />
    </>
  );

  // Single wallet: button that opens modal directly
  if (!hasMultipleWallets) {
    return (
      <>
        <button
          onClick={() => setIsModalOpen(true)}
          className={cn(
            'flex items-center gap-2',
            'rounded-lg bg-bg-secondary px-3 py-2 border border-border',
            'transition-colors hover:border-border-hover',
            className
          )}
          aria-label="View wallet details"
          type="button"
        >
          {trigger}
        </button>
        <WalletDetailModal
          isOpen={isModalOpen}
          onClose={() => setIsModalOpen(false)}
          wallets={wallets}
          selectedAddress={address}
          onSelectWallet={handleModalWalletSelect}
          getWalletTypeLabel={getWalletTypeLabel}
        />
      </>
    );
  }

  // Multiple wallets: dropdown with "Wallet Details" entry + modal
  return (
    <>
      <Dropdown
        items={dropdownItems}
        selectedId={address}
        onSelect={handleWalletSelect}
        trigger={trigger}
        className={className}
        triggerClassName={cn(
          'rounded-lg bg-bg-secondary px-3 py-2 border border-border',
          'transition-colors hover:border-border-hover'
        )}
        ariaLabel="Select wallet"
      />
      <WalletDetailModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        wallets={wallets}
        selectedAddress={address}
        onSelectWallet={handleModalWalletSelect}
        getWalletTypeLabel={getWalletTypeLabel}
      />
    </>
  );
}
